import { Lot, State, Settings } from './types';
import { STORAGE_KEYS } from './storageKeys';
import {
  loadLots,
  saveLots,
  loadSettings as loadSettingsFromStorage,
  saveSettings as saveSettingsToStorage,
  loadStoredAuth,
  saveAuth,
  clearAuth,
  loadStoredViewMode,
  persistViewModePreference,
} from './stateStorage';
import { createLogger } from '../../shared/logger.js';

export { STORAGE_KEYS } from './storageKeys';

// Re-export storage helpers for existing callers (keeps API stable while splitting concerns)
export {
  loadLots,
  saveLots,
  loadStoredAuth,
  saveAuth,
  clearAuth,
  loadStoredViewMode,
  persistViewModePreference,
} from './stateStorage';

const log = createLogger('portal:state');

// View modes
export const VIEW_MODES = {
  USER: 'user',
  SUPERADMIN: 'superadmin',
};

// Sort fields
export const SORT_FIELDS = {
  UPDATED: 'updated',
  CLOSE: 'close',
};

// Default sort: Close time ascending (soonest auctions first)
// User preferences are persisted to localStorage and restored on load
export const DEFAULT_SORT_FIELD = SORT_FIELDS.CLOSE;
export const DEFAULT_SORT_DIRECTION = 'asc';

/**
 * Application state object
 */
export const state: State = {
  mode: 'local', // 'local' | 'auth0'
  lots: [],
  page: 1,
  pageSize: 25,
  q: '',
  filter: { source: '', favorite: '', from: '', to: '' },
  sort: { field: DEFAULT_SORT_FIELD, direction: DEFAULT_SORT_DIRECTION },
  settings: null,
  auth0: null,
  user: null,
  viewMode: VIEW_MODES.USER,
  impersonatedUserId: null, // Super Admin can view another user's lots
  extAccessToken: null,
  extRefreshToken: null,
  extExpiresAt: 0,
  remoteError: '',
  isFetchingRemote: false,
  removingClosedLots: false,
  removingDeadMeat: false,
  pendingPriceReviewIds: new Set(),
  pendingEstimateIds: new Set(),
  pendingBidRefreshIds: new Set(),
  pendingFavoriteIds: new Set(),
  creditLastFetchedAt: 0,
  estimateEdit: null,
  creditBalance: null,
  superadminUsers: [],
};

/**
 * Create default settings object
 */
export function createDefaultSettings(): Settings {
  return {
    timeZone: undefined,
    zipCode: '',
    distanceUnits: 'auto',
    sortField: DEFAULT_SORT_FIELD,
    sortDirection: DEFAULT_SORT_DIRECTION,
    taxRate: 0,
    bidderPremium: 15,
    sellerCosts: 14,
    fixedLotCost: 0,
    // Target profit margin (% of spend) for the walk-away "stay under" ceiling
    targetMarginPct: 30,
    // Group the lot table by auction with rollup header rows
    groupByAuction: false,
    // Optional cap on total open bid commitment (null = no cap)
    budgetCap: null,
    // Closing-soon notification preferences
    enableNotifications: false,
    notifyLeadMinutes: [60, 30, 10], // minutes before close
  };
}

/**
 * Ensure settings object exists and has all required fields
 */
export function ensureSettings(): Settings {
  if (!state.settings || typeof state.settings !== 'object') {
    state.settings = createDefaultSettings();
  } else {
    state.settings = Object.assign(createDefaultSettings(), state.settings);
  }
  return state.settings;
}

/**
 * Load settings from storage and hydrate defaults
 */
export function loadSettings(): Settings {
  const loaded = loadSettingsFromStorage(createDefaultSettings());
  state.settings = Object.assign(createDefaultSettings(), loaded || {});
  return state.settings;
}

/**
 * Save settings after ensuring defaults
 */
export function saveSettings(): void {
  ensureSettings();
  saveSettingsToStorage(state.settings as Settings);
}

export {
  loadCustomCloseTimes,
  saveCustomCloseTimes,
  getEffectiveCloseTime,
  setCustomCloseTime,
  hasCustomCloseTime,
  getCachedEstimate,
  setCachedEstimate,
} from './storage';

/**
 * Persist sort preference
 */
export function persistSortPreference(): void {
  try {
    const settings = ensureSettings();
    settings.sortField = state.sort.field;
    settings.sortDirection = state.sort.direction;
    saveSettings();
  } catch (err) {
    log.warn('Failed to persist sort preference:', err);
  }
}

/**
 * Load all state from storage
 */
export function load(): void {
  state.lots = loadLots();
  state.settings = loadSettings();

  // Restore sort preference
  if (state.settings.sortField) {
    state.sort.field = state.settings.sortField;
  }
  if (state.settings.sortDirection) {
    state.sort.direction = state.settings.sortDirection;
  }

  // Load view mode preference
  const storedViewMode = loadStoredViewMode(VIEW_MODES);
  if (storedViewMode) {
    state.viewMode = storedViewMode;
  }
}

/**
 * Save all state to storage
 */
export function save(): void {
  saveLots(state.lots);
  saveSettings();
}
